import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Scale, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api, errMsg } from "@/lib/api";
import { useClients, ClientSelect } from "@/components/ClientSelect";
import { SiteMap } from "@/components/SiteMap";
import { FranchiseTemplatePicker } from "@/components/FranchiseTemplatePicker";
import { BrandRow, AddBrand, newBrand, brandPayload } from "@/components/BrandRow";

const F = ({ label, children }) => <div className="space-y-2"><Label className="text-xs uppercase tracking-wider text-slate-400">{label}</Label>{children}</div>;
const field = "bg-slate-950/60 border-slate-700 focus-visible:ring-emerald-500";

export default function CompareNew() {
  const [form, setForm] = useState({ client_id: "", name: "", address: "", property_type: "", budget: "", notes: "" });
  const [brands, setBrands] = useState(() => [newBrand(), newBrand()]);
  const [tpl, setTpl] = useState("");
  const [mapAddress, setMapAddress] = useState("");
  const [loading, setLoading] = useState(false);
  const clients = useClients();
  const navigate = useNavigate();
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  useEffect(() => { const t = setTimeout(() => setMapAddress(form.address), 900); return () => clearTimeout(t); }, [form.address]);

  const pickTemplate = (t) => {
    setTpl(t.id);
    setForm((f) => ({ ...f, property_type: t.form.property_type }));
    setBrands((bs) => bs.map((b) => ({ ...b, category: t.form.franchise_category })));
  };
  const addBrandName = (name) => setBrands((bs) => {
    if (bs.some((b) => b.brand === name)) return bs;
    const i = bs.findIndex((b) => !b.brand.trim());
    if (i === -1) return bs.length < 3 ? [...bs, newBrand({ brand: name, category: bs[0].category })] : bs;
    return bs.map((b, j) => (j === i ? { ...b, brand: name } : b));
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.address.trim()) return toast.error("Property address is required");
    if (brands.some((b) => !b.brand.trim())) return toast.error("Enter a name for every brand");
    setLoading(true);
    try {
      const r = await api.post("/comparisons", { ...form, client_id: form.client_id || null, budget: form.budget ? Number(form.budget) : null, brands: brands.map(brandPayload) });
      navigate(`/compare/${r.data.id}`);
    } catch (err) { toast.error(errMsg(err, "Could not start the comparison")); setLoading(false); }
  };

  return (
    <div className="grid-bg min-h-[calc(100vh-4rem)]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14 fade-up">
        <p className="text-xs uppercase tracking-[0.2em] text-emerald-400 font-semibold mb-3">New franchise comparison</p>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight mb-3">Which brand wins here?</h1>
        <p className="text-slate-400 mb-8">Pick 2–3 franchise brands and one address. The AI uses live Google and Census data for the site and compares fees, investment and ROI after royalties.</p>
        <form onSubmit={submit} className="rounded-xl border border-slate-800 bg-slate-900/80 backdrop-blur-md p-6 md:p-8 space-y-6" data-testid="compare-form">
          <F label="Client (optional)"><ClientSelect value={form.client_id} onChange={(v) => setForm((f) => ({ ...f, client_id: v }))} clients={clients} testId="compare-client" /></F>
          <F label="Property address *"><Input data-testid="compare-address" value={form.address} onChange={set("address")} className={field} placeholder="e.g. 1100 S Congress Ave, Austin, TX 78704" /></F>
          <SiteMap address={mapAddress} height={200} testId="compare-new-map" />
          <FranchiseTemplatePicker selected={tpl} onPick={pickTemplate} onBrand={addBrandName} title="Category (fills in popular brands)" />
          <div className="space-y-4">
            {brands.map((b, i) => <BrandRow key={b._k} i={i} b={b} onChange={(nb) => setBrands((bs) => bs.map((x, j) => (j === i ? nb : x)))} onRemove={brands.length > 2 ? () => setBrands((bs) => bs.filter((_, j) => j !== i)) : null} />)}
            {brands.length < 3 && <AddBrand onAdd={() => setBrands((bs) => [...bs, newBrand({ category: bs[0].category })])} />}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <F label="Property type"><Input data-testid="compare-property-type" value={form.property_type} onChange={set("property_type")} className={field} placeholder="e.g. Retail end-cap" /></F>
            <F label="Investor budget (USD)"><Input data-testid="compare-budget" type="number" value={form.budget} onChange={set("budget")} className={`${field} num`} placeholder="600000" /></F>
          </div>
          <F label="Comparison name (optional)"><Input data-testid="compare-name" value={form.name} onChange={set("name")} className={field} placeholder="AI will suggest one" /></F>
          <F label="Notes (optional)"><Textarea data-testid="compare-notes" rows={3} value={form.notes} onChange={set("notes")} className={field} placeholder="Goals, owner-operator or semi-absentee, concerns…" /></F>
          <Button type="submit" data-testid="compare-submit" disabled={loading} className="w-full h-12 rounded-full bg-emerald-500 text-emerald-950 hover:bg-emerald-400 font-semibold">
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Scale className="h-4 w-4 mr-2" />}Compare franchises
          </Button>
        </form>
      </div>
    </div>
  );
}
